import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatPln,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Koszyk: wysuwany drawer zasilany z DRF (GET /api/cart/) + add/remove/checkout.
//
// Scope kontrolera = <body> strony (block body_attrs) — dzięki temu przycisk
// koszyka w nav i przyciski "+ Dodaj" w wierszach tabeli trafiają w akcje.
// Mutacje NIE mają fallbacku "udawanego sukcesu": błąd POST = brak zmiany UI
// (+ komunikat), bo koszyk to rezerwacje i pieniądze.
export default class extends Controller {
  static targets = [
    "overlay",
    "drawer",
    "count",
    "empty",
    "content",
    "success",
    "net",
    "vat",
    "gross",
    "items",
    "error",
    "checkoutBtn",
    "creditRow",
    "creditToggle",
    "creditAvailable",
    "creditApplied",
    "termsModal",
  ];
  static values = {
    count: Number,
    url: String, // GET  /api/cart/
    addUrl: String, // POST /api/cart/add/      {listing_id}
    removeUrl: String, // POST /api/cart/remove/   {listing_id}
    checkoutUrl: String, // POST /api/orders/checkout/
    acceptTermsUrl: String, // POST /api/terms/accept/
  };

  // Czy zastosować kredyt (pomniejsza podstawę VAT). Sumy liczy backend — patrz ?use_credit.
  useCredit = false;

  connect() {
    // Po powrocie z płatności: ?payment=success => pokaż potwierdzenie (nie nadpisuj loadem).
    const params = new URLSearchParams(window.location.search);
    this.showingSuccess = params.get("payment") === "success";
    if (this.showingSuccess) {
      this.open();
      this.#showState("success");
      // Zdejmij parametr z URL — reload/nawigacja wstecz nie ma ponownie pokazywać sukcesu.
      params.delete("payment");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        query ? `?${query}` : window.location.pathname,
      );
    }
    if (this.urlValue) this.load();
  }

  async load() {
    const url = this.useCredit
      ? `${this.urlValue}?use_credit=1`
      : this.urlValue;
    try {
      this.#render(await getJSON(url));
    } catch {
      // np. 400 "Brak organizacji" — zostaw stan pusty; strona dalej działa.
    }
  }

  // Toggle "użyj kredytu" → przelicz sumy na backendzie (source of truth) i przekaż do checkoutu.
  toggleCredit(event) {
    this.useCredit = event.currentTarget.checked;
    this.load();
  }

  open() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = false;
      this.drawerTarget.hidden = false;
    }
  }

  close() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = true;
      this.drawerTarget.hidden = true;
    }
    // Zamknięcie potwierdzenia = koniec stanu sukcesu — drawer wraca do koszyka (nowe zakupy możliwe).
    if (this.showingSuccess) {
      this.showingSuccess = false;
      if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = false;
      this.#showState(this.countValue > 0 ? "content" : "empty");
    }
  }

  // "+ Dodaj" w wierszu ORAZ w modalu podglądu. data-cart-id-param = ID listingu.
  async toggle(event) {
    const id = event.params.id;
    const btn = event.currentTarget;
    const inCart = btn.classList.contains("is-in-cart");
    const url = inCart ? this.removeUrlValue : this.addUrlValue;
    const res = await sendJSON("POST", url, { listing_id: id });
    if (!res.ok) {
      this.#flashError(
        errorDetail(res.data, "Nie udało się zaktualizować koszyka."),
      );
      return;
    }
    this.#reflectButton(btn, !inCart);
    await this.load();
  }

  async remove(event) {
    const id = event.params.id;
    const res = await sendJSON("POST", this.removeUrlValue, { listing_id: id });
    if (!res.ok) return;
    const btn = document.querySelector(
      `.ls-add-btn[data-cart-id-param="${id}"]`,
    );
    if (btn) this.#reflectButton(btn, false);
    await this.load();
  }

  async checkout() {
    if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = true;
    const res = await sendJSON("POST", this.checkoutUrlValue, {
      use_credit: this.useCredit,
    });
    if (res.ok) {
      if (res.data?.redirect_url) {
        // Zewnętrzna bramka (P24): jedziemy na stronę płatności.
        window.location.assign(res.data.redirect_url);
        return;
      }
      // Brak redirectu = płatność uznana od razu (dev AutoPaid / kredyt) — pokaż potwierdzenie.
      this.showingSuccess = true;
      this.#showState("success");
      this.open();
      await this.load(); // koszyk jest już pusty (rezerwacje sprzedane)
      return;
    }
    if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = false;
    // Brama regulaminu (art. 7 RODO): checkout omija middleware /api/, więc backstop jest tu.
    // 403 z code=terms_required → pokaż modal regulaminu zamiast generycznego błędu.
    if (res.status === 403 && res.data?.code === "terms_required") {
      if (this.hasTermsModalTarget) this.termsModalTarget.hidden = false;
      return;
    }
    await this.load(); // rezerwacje mogły wygasnąć — odśwież zawartość
    this.#flashError(
      errorDetail(res.data, "Nie udało się rozpocząć płatności."),
    ); // PO renderze (render czyści stare błędy)
  }

  // Akceptacja regulaminu z modala checkout → zapis akceptacji, zamknięcie modala, ponowny checkout.
  async acceptTerms() {
    const res = await sendJSON("POST", this.acceptTermsUrlValue, {});
    if (!res.ok) {
      this.#flashError("Nie udało się zapisać akceptacji regulaminu.");
      return;
    }
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
    await this.checkout();
  }

  closeTerms() {
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
  }

  // Klik w treść modala nie propaguje do overlaya (który zamyka modal).
  stop(event) {
    event.stopPropagation();
  }

  countValueChanged(value) {
    this.countTargets.forEach((el) => {
      el.textContent = value;
    });
  }

  // --- prywatne ---

  #render(data) {
    this.countValue = data.count;
    if (this.hasErrorTarget) this.errorTarget.hidden = true; // udany render czyści stary komunikat błędu
    if (this.hasNetTarget) this.netTarget.textContent = formatPln(data.net);
    if (this.hasVatTarget) this.vatTarget.textContent = formatPln(data.vat);
    if (this.hasGrossTarget)
      this.grossTarget.textContent = formatPln(data.gross);
    this.#renderCredit(data);

    if (this.hasItemsTarget) {
      this.itemsTarget.textContent = "";
      const template = document.getElementById("cart-item-template");
      for (const item of listResults(data)) {
        const row = template.content.firstElementChild.cloneNode(true);
        row.dataset.cartItem = item.id;
        row.querySelector('[data-field="cat"]').textContent =
          item.category_path;
        row.querySelector('[data-field="code"]').textContent = item.code;
        row.querySelector('[data-field="price"]').textContent = formatPln(
          item.price_pln,
        );
        row
          .querySelector("[data-action='cart#remove']")
          .setAttribute("data-cart-id-param", item.id);
        this.itemsTarget.appendChild(row);
      }
    }
    if (!this.showingSuccess)
      this.#showState(data.count > 0 ? "content" : "empty");
  }

  // Wiersz kredytu: widoczny tylko gdy org ma saldo > 0. Applied (ujemne) pokazujemy gdy toggle aktywny.
  #renderCredit(data) {
    if (!this.hasCreditRowTarget) return;
    const available = Number(data.credit_available ?? 0);
    this.creditRowTarget.hidden = available <= 0;
    if (available <= 0) {
      this.useCredit = false;
      if (this.hasCreditToggleTarget) this.creditToggleTarget.checked = false;
      return;
    }
    if (this.hasCreditAvailableTarget)
      this.creditAvailableTarget.textContent = formatPln(data.credit_available);
    if (this.hasCreditToggleTarget)
      this.creditToggleTarget.checked = this.useCredit;
    if (this.hasCreditAppliedTarget) {
      const applied = Number(data.credit_applied ?? 0);
      this.creditAppliedTarget.hidden = applied <= 0;
      this.creditAppliedTarget.textContent =
        applied > 0 ? `−${formatPln(data.credit_applied)}` : "";
    }
  }

  #reflectButton(btn, inCart) {
    btn.classList.toggle("is-in-cart", inCart);
    const glyph = btn.querySelector(".ls-mono");
    const label = btn.querySelector("[data-cart-label]");
    if (glyph) glyph.textContent = inCart ? "✓" : "+";
    if (label) label.textContent = inCart ? "W koszyku" : "Dodaj";
    const row = btn.closest(".ls-lead-row");
    if (row) row.classList.toggle("is-selected", inCart);
  }

  #showState(state) {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = state !== "empty";
    if (this.hasContentTarget) this.contentTarget.hidden = state !== "content";
    if (this.hasSuccessTarget) this.successTarget.hidden = state !== "success";
  }

  #flashError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }
}
